export interface Me {
  steamId: string;
  displayName: string;
  avatarUrl: string | null;
  profileUrl: string | null;
  lastLibrarySyncAt: string | null;
}

export interface RoomMemberSummary {
  steamId: string;
  displayName: string;
  avatarUrl: string | null;
  profileUrl: string | null;
  lastLibrarySyncAt: string | null;
  joinedAt: string | null;
}

export interface CommonGame {
  appId: number;
  name: string;
  iconUrl: string | null;
  custom?: boolean;
  addedBySteamId?: string | null;
}

export const CUSTOM_NAME_MAX = 80;

export function isCustomAppId(appId: number): boolean {
  return appId < 0;
}

export function compareGamesByName(a: CommonGame, b: CommonGame): number {
  return a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
}

export const ROOM_MODES = ["normal", "elimination"] as const;
export type RoomMode = (typeof ROOM_MODES)[number];

export interface MatchNode {
  id: string;
  round: number;
  index: number;
  playerA: string | null;
  playerB: string | null;
  winner: string | null;
}

export type TournamentStatus = "idle" | "active" | "complete";
export type TournamentPhase = "pick_game" | "awaiting_result" | "complete";

export type TournamentFormat = "bracket" | "solo";

export interface TournamentObjectiveCatalogEntry {
  id: string;
  text: string;
  appId: number | null;
}

export interface TournamentObjectivePick {
  id: string;
  text: string;
  appId: number | null;
}

export interface TournamentState {
  status: TournamentStatus;
  phase: TournamentPhase;
  format: TournamentFormat;
  nodes: MatchNode[];
  currentMatchId: string | null;
  selectedAppId: number | null;
  objective: TournamentObjectivePick | null;
  seeds: string[];
  objectiveCatalog: TournamentObjectiveCatalogEntry[];
  tournamentWheelExclusions: number[];
  /** Present when tournament is active in pick_game / awaiting_result */
  currentMutualGames?: CommonGame[];
}

export interface RoomDetails {
  code: string;
  ownerSteamId: string;
  createdAt: string;
  mode: RoomMode;
  members: RoomMemberSummary[];
  commonGames: CommonGame[];
  disabledAppIds: number[];
}

export const API_BASE: string = (import.meta.env.VITE_API_URL ?? "").replace(/\/+$/, "");

function withBase(path: string): string {
  if (/^https?:\/\//i.test(path)) return path;
  if (!API_BASE) return path;
  return `${API_BASE}${path.startsWith("/") ? path : `/${path}`}`;
}

async function request<T>(input: string, init?: RequestInit): Promise<T> {
  const res = await fetch(withBase(input), {
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    ...init,
  });
  if (res.status === 401) {
    throw new ApiError("unauthorized", 401);
  }
  if (!res.ok) {
    let message = res.statusText;
    try {
      const body = (await res.json()) as { error?: string };
      if (body.error) message = body.error;
    } catch {
      // ignore
    }
    throw new ApiError(message, res.status);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
  ) {
    super(message);
  }
}

export const api = {
  async me(): Promise<Me | null> {
    try {
      return await request<Me>("/api/me");
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) return null;
      throw err;
    }
  },
  syncLibrary(): Promise<{ ok: true; count: number; syncedAt: string }> {
    return request("/api/me/sync", { method: "POST" });
  },
  logout(): Promise<{ ok: true }> {
    return request("/auth/logout", { method: "POST" });
  },
  createRoom(): Promise<{ code: string }> {
    return request("/api/rooms", { method: "POST" });
  },
  joinRoom(code: string): Promise<RoomDetails> {
    return request(`/api/rooms/${encodeURIComponent(code)}/join`, { method: "POST" });
  },
  leaveRoom(code: string): Promise<{ ok: true }> {
    return request(`/api/rooms/${encodeURIComponent(code)}/leave`, { method: "POST" });
  },
  getRoom(code: string): Promise<RoomDetails> {
    return request(`/api/rooms/${encodeURIComponent(code)}`);
  },
  getTournament(code: string): Promise<TournamentState> {
    return request(`/api/rooms/${encodeURIComponent(code)}/tournament`);
  },
  startTournament(code: string): Promise<TournamentState> {
    return request(`/api/rooms/${encodeURIComponent(code)}/tournament/start`, {
      method: "POST",
    });
  },
  startSoloTournament(code: string): Promise<TournamentState> {
    return request(`/api/rooms/${encodeURIComponent(code)}/tournament/start-solo`, {
      method: "POST",
    });
  },
  addTournamentObjective(
    code: string,
    body: { text: string; appId: number | null },
  ): Promise<{ objectiveCatalog: TournamentObjectiveCatalogEntry[] }> {
    return request(`/api/rooms/${encodeURIComponent(code)}/tournament/objectives`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
  updateTournamentObjective(
    code: string,
    objectiveId: string,
    body: { text?: string; appId?: number | null },
  ): Promise<{ objectiveCatalog: TournamentObjectiveCatalogEntry[] }> {
    return request(
      `/api/rooms/${encodeURIComponent(code)}/tournament/objectives/${encodeURIComponent(objectiveId)}`,
      { method: "PATCH", body: JSON.stringify(body) },
    );
  },
  deleteTournamentObjective(
    code: string,
    objectiveId: string,
  ): Promise<{ objectiveCatalog: TournamentObjectiveCatalogEntry[] }> {
    return request(
      `/api/rooms/${encodeURIComponent(code)}/tournament/objectives/${encodeURIComponent(objectiveId)}`,
      { method: "DELETE" },
    );
  },
  setTournamentWheelExclusion(
    code: string,
    body: { appId: number; excluded: boolean },
  ): Promise<{ tournamentWheelExclusions: number[] }> {
    return request(`/api/rooms/${encodeURIComponent(code)}/tournament/exclusions`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
};

export const steamLoginUrl = withBase("/auth/steam");
